import { Button, Paper, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Grid, Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { useState, useMemo, useEffect } from "react";

import "prismjs/components/prism-sql";
import "prismjs/themes/prism.css";
import DataStoreRequestRunQueryInputOutputFields from "./DataStoreRequestRunQueryInputOutputFields";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import LoopIcon from "@mui/icons-material/Loop";
import SqlEditor from "../SqlEditor/SqlEditor";
import { DataStoreReqEndPointLogic } from "../../../../logic/Admin/DataStoreRequest/DataStoreRequestEndPointLogic";
import { selectUserToken } from "../../../../features/userSlice";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Autocomplete, Chip } from "@mui/material";

interface InputProperty {
  format: string;
  title: string;
  type: string;
  description: string;
  order: number;
  value_for_test: string;
}

const DataStoreRequestRunQueryDetails = () => {
  const { queryContentSingle, runQueryButtonLoading } = useSelector(
    (state: any) => state.dataStoreReq
  );
  const { query, input } = queryContentSingle ?? {};

  const [inputValues, setInputValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const token = useSelector(selectUserToken);
  const dispatch = useDispatch();
  const [allSharedUids, setAllSharedUids] = useState<string[]>([]);

  // Sort properties by order and create sorted array
  const sortedInputFields = useMemo(() => {
    if (!input?.properties) return [];

    return Object.entries(input.properties)
      .map(([key, property]: [string, any]) => ({
        key, // preserve the key
        order: property.order ?? 0, // default order if missing
        ...property,
      }))
      .sort((a, b) => a.order - b.order);
  }, [input]);

  function handleClick() {
    setLoading(true);
  }

  // Initialize all fields once input changes
  useMemo(() => {
    if (sortedInputFields.length > 0) {
      const initialValues: Record<string, any> = {};
      sortedInputFields.forEach((field, idx) => {
        initialValues[`${idx}-title`] = field.title;
        initialValues[`${idx}-description`] = field.description;
        initialValues[`${idx}-format`] = field.format;
        initialValues[`${idx}-order`] = field.order;
        initialValues[`${idx}-value_for_test`] =
          field.value_for_test || "value_for_test";
        initialValues[`${idx}-type`] = field.type;
        initialValues[`${idx}-key`] = field.key;
      });
      setInputValues(initialValues);
    }
  }, [sortedInputFields]);

  useEffect(() => {
    sortedInputFields.forEach((field) => {
      if (field.format === "array" && field.value_for_test) {
        const initialUids = Array.isArray(field.value_for_test)
          ? field.value_for_test
          : [];

        // merge into master pool (no duplicates)
        setAllSharedUids((prev) =>
          Array.from(new Set([...prev, ...initialUids]))
        );
      }
    });
  }, [sortedInputFields]);

  const allInputsFilled = useMemo(() => {
    const valueKeys = Object.keys(inputValues).filter((k) =>
      k.endsWith("-value_for_test")
    );

    if (valueKeys.length === 0) return false;

    return valueKeys.every((key) => {
      const val = inputValues[key];
      return val !== undefined && val !== null && String(val).trim() !== "";
    });
  }, [inputValues]);

  const normalizeValue = (val: any, type: string) => {
    if (type === "string" && typeof val === "string") {
      return val.replace(/^['"]|['"]$/g, ""); // remove quotes
    }
    return val ?? ""; // fallback for number, null, undefined
  };

  const handleInputChange = (idx: number, newValue: any) => {
    setInputValues((prev) => ({
      ...prev,
      [`${idx}-value_for_test`]: newValue, // ✅ only update value
    }));
  };

  const renderInputField = (
    field: InputProperty & { key: string },
    idx: number
  ) => {
    const { type, title, value_for_test, format } = field;

    switch (format) {
      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={title}
              value={
                inputValues[`${idx}-value_for_test`]
                  ? dayjs(inputValues[`${idx}-value_for_test`])
                  : null
              }
              onChange={(newValue) =>
                handleInputChange(
                  idx,
                  newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                )
              }
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  color: "secondary",
                },
              }}
              format="DD-MM-YYYY"
            />
          </LocalizationProvider>
        );

      case "array":
        const selectedUids = Array.isArray(inputValues[`${idx}-value_for_test`])
          ? inputValues[`${idx}-value_for_test`]
          : [];

        return (
          <Autocomplete
            multiple
            options={allSharedUids} // 🔹 fixed pool that never shrinks
            freeSolo
            value={selectedUids}
            onChange={(_, newValue) => {
              // if user typed a new UID that wasn’t in the pool → add it permanently
              setAllSharedUids((prev) =>
                Array.from(new Set([...prev, ...newValue]))
              );
              handleInputChange(idx, newValue);
            }}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="filled"
                  label={option}
                  {...getTagProps({ index })}
                  color="secondary"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={title}
                placeholder="Select or type UID"
                fullWidth
                size="small"
                color="secondary"
              />
            )}
          />
        );

      case "number":
        return (
          <TextField
            label={title}
            type="number"
            fullWidth
            size="small"
            color="secondary"
            defaultValue={value_for_test}
            onChange={(e) => handleInputChange(idx, e.target.value)}
          />
        );

      default:
        return (
          <TextField
            label={title}
            placeholder="value_for_test"
            fullWidth
            size="small"
            color="secondary"
            defaultValue={normalizeValue(value_for_test, type)}
            onChange={(e) => handleInputChange(idx, e.target.value)}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        );
    }
  };
  const handleRunQuery = async () => {
    try {
      await DataStoreReqEndPointLogic.runBizLayerQuery(
        token,
        dispatch,
        queryContentSingle?.id,
        inputValues
      );
    } catch (error) {
      console.error("Error running query:", error);
    }
  };

  return (
    <>
      <Paper sx={{ padding: "16px", mb: 2 }}>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid size={12}>
            <Typography fontSize={24}>Run Details</Typography>
          </Grid>

          {/* Dynamic input fields */}
          {sortedInputFields.map((field, idx) => (
            <Grid size={3} key={field.key}>
              {renderInputField(field, idx)}
            </Grid>
          ))}

          <Grid size={12}>
            <SqlEditor value={query} readOnly={true} />
          </Grid>
        </Grid>
        <DataStoreRequestRunQueryInputOutputFields inputValues={inputValues} />

        <Stack
          sx={{
            display: "flex",
            justifyContent: "end",
            flexDirection: "row",
            mt: 2,
          }}
        >
          {runQueryButtonLoading ? (
            <Button
              onClick={handleClick}
              endIcon={<LoopIcon />}
              loading={loading}
              loadingPosition="end"
              variant="contained"
              sx={{ whiteSpace: "nowrap", width: "135px", ml: "10px" }}
            >
              Running
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              sx={{ whiteSpace: "nowrap", width: "135px", ml: "10px" }}
              onClick={handleRunQuery}
              disabled={!allInputsFilled}
              data-tracker="426"
            >
              run query <PlayCircleIcon sx={{ width: "20px", ml: "5px" }} />
            </Button>
          )}
        </Stack>
      </Paper>
    </>
  );
};

export default DataStoreRequestRunQueryDetails;
